/**
 * Formulário de uma oficina da agenda.
 *
 * Papel: preenche, valida e grava uma oficina — nome, serviço, telefone,
 * e-mail, endereço e observação —, servindo tanto à criação quanto à edição.
 *
 * Como usar: quem monta este componente passa `oficina` para editar ou `null`
 * para criar, e deverá dar a ele uma `key` diferente por oficina. Sem a `key`,
 * o React reaproveita a mesma instância ao trocar de registro e os campos
 * continuam com o que a pessoa via antes.
 *
 * Só o nome é obrigatório. Uma oficina de quem se tem apenas o nome e o
 * telefone precisa poder ser cadastrada sem que se invente o resto.
 */
import { type NovaOficina, type Oficina, novaOficinaSchema } from '@concierge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, type FieldPath, useForm } from 'react-hook-form';

import { aplicarErroDaApi, tradutorPorMapa } from '@/shared/lib/erro-de-formulario';
import { Button } from '@/shared/ui/button';
import { CampoMascarado } from '@/shared/ui/campo-mascarado';
import { DialogFooter } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { useSalvarOficina } from '../hooks/usar-veiculos';
import { OFICINA_EM_BRANCO, type OficinaNoFormulario } from '../types';

interface FormularioOficinaProps {
  /** A oficina a editar, ou `null` para criar uma nova. */
  oficina: Oficina | null;
  onSalvo: () => void;
  onCancelar: () => void;
}

/** Os campos que a API pode apontar, cada um no caminho do formulário. */
const CAMPOS: Record<string, FieldPath<OficinaNoFormulario> | undefined> = {
  nome: 'nome',
  servico: 'servico',
  telefone: 'telefone',
  email: 'email',
  endereco: 'endereco',
  observacao: 'observacao',
};

/**
 * Converte a oficina vinda da API no formato que o formulário guarda.
 *
 * @param oficina - A oficina a editar, ou `null` para começar em branco.
 * @returns Os valores iniciais, com `null` trocado por string vazia.
 */
function valoresIniciais(oficina: Oficina | null): OficinaNoFormulario {
  if (oficina === null) return { ...OFICINA_EM_BRANCO };

  return {
    nome: oficina.nome,
    servico: oficina.servico ?? '',
    telefone: oficina.telefone ?? '',
    email: oficina.email ?? '',
    endereco: oficina.endereco ?? '',
    observacao: oficina.observacao ?? '',
  };
}

/**
 * O formulário de criação e de edição de oficina.
 *
 * @param oficina - A oficina a editar, ou `null` para criar.
 * @param onSalvo - Chamado depois de a API confirmar a gravação.
 * @param onCancelar - Chamado quando se desiste, sem gravar nada.
 * @returns O formulário completo, com os botões de ação.
 */
export function FormularioOficina({ oficina, onSalvo, onCancelar }: FormularioOficinaProps) {
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const salvar = useSalvarOficina();

  const form = useForm<OficinaNoFormulario, unknown, NovaOficina>({
    resolver: zodResolver(novaOficinaSchema),
    defaultValues: valoresIniciais(oficina),
  });

  const { errors } = form.formState;
  const enviando = salvar.isPending;

  /** Grava a oficina e leva o erro da API para o campo que o causou. */
  const handleSalvar = form.handleSubmit(async (dados) => {
    setErroGeral(null);
    try {
      await salvar.mutateAsync({ id: oficina?.id ?? null, dados });
      onSalvo();
    } catch (erro) {
      setErroGeral(aplicarErroDaApi(erro, form, tradutorPorMapa(CAMPOS)));
    }
  });

  return (
    <form onSubmit={handleSalvar}>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="oficina-nome">Nome</Label>
          <Input
            id="oficina-nome"
            autoComplete="off"
            disabled={enviando}
            {...form.register('nome')}
          />
          {errors.nome && (
            <p className="text-sm text-destructive" role="alert">
              {errors.nome.message}
            </p>
          )}
        </div>

        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="oficina-servico">Serviço</Label>
          <Input
            id="oficina-servico"
            autoComplete="off"
            placeholder="Mecânica, blindagem, pneus…"
            disabled={enviando}
            {...form.register('servico')}
          />
          {errors.servico && (
            <p className="text-sm text-destructive" role="alert">
              {errors.servico.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="oficina-telefone">Telefone</Label>
          <Controller
            control={form.control}
            name="telefone"
            render={({ field }) => (
              <CampoMascarado
                id="oficina-telefone"
                formato="telefone"
                value={field.value}
                onChange={field.onChange}
                disabled={enviando}
              />
            )}
          />
          {errors.telefone && (
            <p className="text-sm text-destructive" role="alert">
              {errors.telefone.message}
            </p>
          )}
        </div>

        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="oficina-email">E-mail</Label>
          <Input
            id="oficina-email"
            type="email"
            autoComplete="off"
            disabled={enviando}
            {...form.register('email')}
          />
          {errors.email && (
            <p className="text-sm text-destructive" role="alert">
              {errors.email.message}
            </p>
          )}
        </div>

        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="oficina-endereco">Endereço</Label>
          <Input
            id="oficina-endereco"
            autoComplete="off"
            disabled={enviando}
            {...form.register('endereco')}
          />
          {errors.endereco && (
            <p className="text-sm text-destructive" role="alert">
              {errors.endereco.message}
            </p>
          )}
        </div>

        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="oficina-observacao">Observações</Label>
          <Textarea
            id="oficina-observacao"
            rows={3}
            disabled={enviando}
            {...form.register('observacao')}
          />
          {errors.observacao && (
            <p className="text-sm text-destructive" role="alert">
              {errors.observacao.message}
            </p>
          )}
        </div>

        {erroGeral !== null && (
          <p className="col-span-full text-sm text-destructive" role="alert">
            {erroGeral}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="ghost" disabled={enviando} onClick={onCancelar}>
          Cancelar
        </Button>
        <Button type="submit" disabled={enviando}>
          {enviando ? 'Salvando…' : 'Salvar contato'}
        </Button>
      </DialogFooter>
    </form>
  );
}
